import { useCallback, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { logout as requestLogout } from '@/api/authApi';
import { logout as clearAuthentication } from '@/store/authSlice';
import { useAppDispatch } from '@/store/hooks';
import { storage } from '@/utils/storage';
import { clearAuthenticationSession } from '../services/authSession';

interface UseLogoutResult {
  isLoggingOut: boolean;
  signOut: () => Promise<void>;
}

const useLogout = (): UseLogoutResult => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const signOut = useCallback(async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    const token = storage.getAccessToken();
    let remoteLogoutSucceeded = false;

    try {
      if (token) {
        await requestLogout(token);
      }
      remoteLogoutSucceeded = true;
    } catch {
      remoteLogoutSucceeded = false;
    } finally {
      clearAuthenticationSession();
      dispatch(clearAuthentication());
      navigate('/login', { replace: true });
      if (remoteLogoutSucceeded) {
        message.success(t('account.notices.logged_out'));
      } else {
        message.warning(t('account.notices.logged_out_locally'));
      }
      setIsLoggingOut(false);
    }
  }, [dispatch, isLoggingOut, navigate, t]);

  return { isLoggingOut, signOut };
};

export default useLogout;
